import React, { useMemo } from "react";


const sampleRows = [
  { date: "01/04/2021", reference: "OPENING BALANCE", type: "", no: "", chequeNo: "", chequeDate: "", drAmount: "145,365.00", crAmount: "0.00" },
  { date: "12/04/2021", reference: "BILL FOR APRIL", type: "", no: "1", chequeNo: "", chequeDate: "", drAmount: "5,630.00", crAmount: "0.00" },
  { date: "30/04/2021", reference: "Receipt: India overseas bank", type: "", no: "11", chequeNo: "45753", chequeDate: "30/04/2021", drAmount: "0.00", crAmount: "4,157.00" },
  { date: "10/05/2021", reference: "BILL FOR MAY", type: "", no: "40", chequeNo: "", chequeDate: "", drAmount: "5,630.00", crAmount: "0.00" },
  { date: "20/05/2021", reference: "Receipt: India overseas bank", type: "", no: "11", chequeNo: "45124", chequeDate: "20/05/2021", drAmount: "0.00", crAmount: "4,157.00" },
  { date: "30/05/2021", reference: "Receipt: India overseas bank", type: "", no: "4", chequeNo: "", chequeDate: "", drAmount: "10,328.00", crAmount: "0.00" },
];

function parseNumber(v) {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  const cleaned = String(v).replace(/[^0-9.-]+/g, "");
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
}
function fmtNumber(v) {
  try {
    return new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
  } catch {
    return Number(v).toFixed ? Number(v).toFixed(2) : String(v);
  }
}

export default function LedgerTable({ rows = sampleRows, openingBalance = null }) {
  const safeRows = Array.isArray(rows) ? rows : [];

  // infer opening if not provided
  const opening = useMemo(() => {
    if (openingBalance != null) return parseNumber(openingBalance);
    const op = safeRows.find((r) => /opening balance/i.test(String(r.reference || "")));
    return op ? parseNumber(op.drAmount) - parseNumber(op.crAmount) : 0;
  }, [safeRows, openingBalance]);

  // compute running balance (debit increases)
  const rowsWithBalance = useMemo(() => {
    let bal = opening;
    return safeRows.map((r) => {
      const dr = parseNumber(r.drAmount);
      const cr = parseNumber(r.crAmount);
      bal = bal + dr - cr;
      return { ...r, dr, cr, running: bal };
    });
  }, [safeRows, opening]);

  const totals = useMemo(() => {
    const dr = rowsWithBalance.reduce((s, r) => s + (r.dr || 0), 0);
    const cr = rowsWithBalance.reduce((s, r) => s + (r.cr || 0), 0);
    const last = rowsWithBalance.length ? rowsWithBalance[rowsWithBalance.length - 1].running : opening;
    return { dr, cr, last };
  }, [rowsWithBalance, opening]);

  function exportCSV() {
    const header = ["Date", "Reference", "Type", "No", "Cheque No", "Cheque Date", "Dr Amount", "Cr Amount", "Balance"];
    const lines = [header.join(",")];
    rowsWithBalance.forEach((r) => {
      const row = [
        r.date || "",
        `"${(r.reference || "").replace(/"/g, '""')}"`,
        r.type || "",
        r.no || "",
        r.chequeNo || "",
        r.chequeDate || "",
        r.dr ? fmtNumber(r.dr) : "0.00",
        r.cr ? fmtNumber(r.cr) : "0.00",
        fmtNumber(r.running),
      ];
      lines.push(row.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ledger.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="min-h-screen bg-white p-6 font-sans text-slate-800">
      {/* container */}
      <div className="mx-auto max-w-[1100px] border border-slate-200 rounded-sm overflow-hidden shadow-sm">
        {/* tiny centered header with icons on the right (icons visually small like screenshot) */}
        <div className="relative border-b border-slate-200 py-3">
          <div className="text-center">
            <div className="text-[11px] uppercase text-slate-600">Unit Wise General Ledger</div>
            <div className="text-[10px] text-slate-500">For The Year 2021-2022</div>
            <div className="text-[10px] text-slate-500">01/04/2021 to 31/03/2022</div>
          </div>

          <div className="absolute right-3 top-2 flex items-center gap-2">
            <button onClick={() => window.history.back()} className="p-1 rounded hover:bg-slate-100" title="Back">
              {/* back icon (chevrons) */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>

            <button onClick={exportCSV} className="p-1 rounded hover:bg-slate-100" title="Export CSV">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 3v12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M8 11l4 4 4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><rect x="3" y="17" width="18" height="4" rx="1" stroke="currentColor" strokeWidth="1.2"/></svg>
            </button>

            <button onClick={() => window.print()} className="p-1 rounded hover:bg-slate-100" title="Print">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6 9V4h12v5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><rect x="6" y="13" width="12" height="8" rx="1" stroke="currentColor" strokeWidth="1.2"/><path d="M6 17h12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>

            <button className="p-1 rounded hover:bg-slate-100" title="Delete (placeholder)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M3 6h18" stroke="currentColor" strokeWidth="1.2"/><path d="M8 6v14a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6" stroke="currentColor" strokeWidth="1.2"/><path d="M10 11v6" stroke="currentColor" strokeWidth="1.2"/><path d="M14 11v6" stroke="currentColor" strokeWidth="1.2"/><path d="M9 6V4h6v2" stroke="currentColor" strokeWidth="1.2"/></svg>
            </button>
          </div>
        </div>

        {/* table area */}
        <div className="p-3">
          <div className="overflow-x-auto">
            <table className="min-w-[980px] w-full table-fixed text-[12px] border-collapse">
              <thead>
                <tr className="text-[11px] text-slate-600">
                  <th className="w-[90px] text-left pb-2 border-b border-slate-200">DATE</th>
                  <th className="w-[320px] text-left pb-2 border-b border-slate-200">REFERENCE</th>
                  <th className="w-[60px] text-left pb-2 border-b border-slate-200">TYPE</th>
                  <th className="w-[45px] text-left pb-2 border-b border-slate-200">NO</th>
                  <th className="w-[85px] text-left pb-2 border-b border-slate-200">CHEQUE NO</th>
                  <th className="w-[85px] text-left pb-2 border-b border-slate-200">DATE</th>
                  <th className="w-[120px] text-right pb-2 border-b border-slate-200">DR AMOUNT</th>
                  <th className="w-[120px] text-right pb-2 border-b border-slate-200">CR AMOUNT</th>
                  <th className="w-[120px] text-right pb-2 border-b border-slate-200">BALANCE</th>
                </tr>
              </thead>

              <tbody>
                {/* unit header row - dashed underline like screenshot */}
                <tr>
                  <td colSpan={9} className="py-2 border-b border-dashed border-slate-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-[11px] text-slate-500">UNIT NO A 902</div>
                        <div className="text-[13px] font-semibold">MR. Ashok Shriram Dangi & Mrs. Bindu Ashok Dangi</div>
                      </div>
                      <div className="text-[13px] font-semibold">{fmtNumber(opening)}</div>
                    </div>
                  </td>
                </tr>

                {/* optional spacing small row to visually match screenshot */}
                <tr><td colSpan={9} className="h-1" /></tr>

                {/* rows */}
                {rowsWithBalance.map((r, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? "" : "bg-slate-50"}>
                    <td className="py-2 align-top text-[11px]">{r.date}</td>
                    <td className="py-2 align-top text-[11px]">{r.reference}</td>
                    <td className="py-2 align-top text-[11px]">{r.type}</td>
                    <td className="py-2 align-top text-[11px]">{r.no}</td>
                    <td className="py-2 align-top text-[11px]">{r.chequeNo}</td>
                    <td className="py-2 align-top text-[11px]">{r.chequeDate}</td>
                    <td className="py-2 align-top text-[11px] text-right">{r.dr ? fmtNumber(r.dr) : "0.00"}</td>
                    <td className="py-2 align-top text-[11px] text-right">{r.cr ? fmtNumber(r.cr) : "0.00"}</td>
                    <td className="py-2 align-top text-[11px] text-right">{fmtNumber(r.running)}</td>
                  </tr>
                ))}

                {/* totals row */}
                <tr className="border-t border-slate-200">
                  <td colSpan={6} className="py-2 text-[11px]">&nbsp;</td>
                  <td className="py-2 text-right font-semibold text-[11px]">{fmtNumber(totals.dr)}</td>
                  <td className="py-2 text-right font-semibold text-[11px]">{fmtNumber(totals.cr)}</td>
                  <td className="py-2 text-right font-semibold text-[11px]">{fmtNumber(totals.last)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-2 text-[11px] text-slate-500">Note: Replace the sample rows with your data. Table is optimized for landscape/print.</div>
        </div>
      </div>
    </div>
  );
}
